/**
 * The operations app's root: providers and routes.
 *
 * Provider order matters. Data fetching (React Query) is outermost so every
 * page can use it; the router sits inside it; `BrandingProvider` wraps
 * `AuthProvider`, so branding is available to the login page as well;
 * `RequireAuth` guards everything except `/login`, and a 401 anywhere routes
 * through the session-expired flow. Unknown paths fall back to the dashboard.
 *
 * Each page maps one-to-one onto a backend area (see docs/ARCHITECTURE.md,
 * "Frontend applications"); permission checks hide controls, but the server
 * enforces every one of them regardless.
 */

import type { ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AppChrome } from "./AppChrome";
import { AuthProvider } from "./auth/AuthProvider";
import { BrandingProvider } from "./branding";
import { RequireAuth } from "./components/RequireAuth";
import { ButtonsPage } from "./pages/ButtonsPage";
import { CdrDetailPage } from "./pages/CdrDetailPage";
import { CdrPage } from "./pages/CdrPage";
import { DashboardPage } from "./pages/DashboardPage";
import { DevicesPage } from "./pages/DevicesPage";
import { SwapPage } from "./pages/SwapPage";
import { DidsPage } from "./pages/DidsPage";
import { E911Page } from "./pages/E911Page";
import { LoginPage } from "./pages/LoginPage";
import { ActivityPage } from "./pages/ActivityPage";
import { ChangeSetsPage } from "./pages/ChangeSetsPage";
import { MigrationPage } from "./pages/MigrationPage";
import { PairingPage } from "./pages/PairingPage";
import "./styles.css";

const queryClient = new QueryClient({
  // refetchOnWindowFocus stays ON: returning to an idle tab refreshes stale
  // views immediately, and any 401 routes through the session-expired flow.
  defaultOptions: { queries: { retry: 1 } },
});

export function App(): ReactNode {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <BrandingProvider>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route element={<RequireAuth />}>
                <Route path="/" element={<AppChrome />}>
                  <Route index element={<DashboardPage />} />
                  <Route path="cdr" element={<CdrPage />} />
                  <Route path="cdr/:cdrId" element={<CdrDetailPage />} />
                  <Route path="pairing" element={<PairingPage />} />
                  <Route path="dids" element={<DidsPage />} />
                  <Route path="devices" element={<DevicesPage />} />
                  <Route path="swap" element={<SwapPage />} />
                  <Route path="buttons" element={<ButtonsPage />} />
                  <Route path="e911" element={<E911Page />} />
                  <Route path="activity" element={<ActivityPage />} />
                  <Route path="changes" element={<ChangeSetsPage />} />
                  <Route path="migration" element={<MigrationPage />} />
                </Route>
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthProvider>
        </BrandingProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
